import { useEffect, useRef } from 'react';
import { Box, Stack } from '@mui/material';

import { SecondBrainChat, SecondBrainContextNote } from '../types';
import { AnswerCard } from './AnswerCard';

interface SecondBrainChatViewProps {
  activeChat: SecondBrainChat;
  isStreaming: boolean;
  sources: SecondBrainContextNote[];
  isFetchingSources: boolean;
  onOpenNote: (noteId: string) => void;
}

export const SecondBrainChatView = ({
  activeChat,
  isStreaming,
  sources,
  isFetchingSources,
  onOpenNote,
}: SecondBrainChatViewProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const userHasScrolledRef = useRef(false);

  // Only real user input (wheel up / touch drag) disables auto-scroll — the
  // smooth programmatic scroll fires 'scroll' events too, and mid-stream its
  // target is already stale, so position alone can't identify user intent.
  // Reaching the bottom re-enables following.
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (Math.abs(scrollHeight - clientHeight - scrollTop) < 10) {
        userHasScrolledRef.current = false;
      }
    };
    const handleWheel = (event: WheelEvent) => {
      if (event.deltaY < 0) {
        userHasScrolledRef.current = true;
      }
    };
    const handleTouchMove = () => {
      userHasScrolledRef.current = true;
    };

    container.addEventListener('scroll', handleScroll);
    container.addEventListener('wheel', handleWheel);
    container.addEventListener('touchmove', handleTouchMove);
    return () => {
      container.removeEventListener('scroll', handleScroll);
      container.removeEventListener('wheel', handleWheel);
      container.removeEventListener('touchmove', handleTouchMove);
    };
  }, []);

  // Follow the streamed answer unless the user scrolled away. Opening an
  // existing chat (not streaming) stays at the top so the question reads first.
  useEffect(() => {
    if (!isStreaming || userHasScrolledRef.current) return;
    const container = scrollRef.current;
    container?.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
  }, [activeChat.answer, isStreaming, sources.length]);

  const userMessage = activeChat.question ?? activeChat.messages.find((message) => message.role === 'user')?.content;

  return (
    <Box ref={scrollRef} sx={{ flex: 1, overflow: 'auto', px: { xs: 2, sm: 3 }, pt: 4, pb: 3 }}>
      <Stack spacing={3} sx={{ maxWidth: 760, mx: 'auto' }}>
        {userMessage && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Box
              sx={{
                maxWidth: { xs: '85%', sm: '70%' },
                bgcolor: 'primary.main',
                color: 'common.white',
                borderRadius: '18px 18px 6px 18px',
                p: '12px 18px',
                fontSize: 15,
                lineHeight: 1.5,
                letterSpacing: '0.15px',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {userMessage}
            </Box>
          </Box>
        )}
        <AnswerCard
          chat={activeChat}
          isStreaming={isStreaming}
          sources={sources}
          isFetchingSources={isFetchingSources}
          onOpenNote={onOpenNote}
        />
      </Stack>
    </Box>
  );
};

export default SecondBrainChatView;
